import { Controller } from "@hotwired/stimulus"

/**
 * **The cascading selects of the annale wizard** — §11 of the specification, 2026-09-20.
 *
 * > *"I would build a dynamic wizard in 6 steps. The fields appear according to the previous
 * > choices."*
 *
 * ## 🔴 The controller decides NOTHING about which fields to show
 *
 * Every answer comes from `GET /console/annales/referential`, which returns what
 * `Go4learn::Exams::WizardReferential` reads from the referential. A first draft that had
 * mapped exam shortcodes to field sets here would be a second wording of §11 — one in Ruby,
 * one in JavaScript — and the two would diverge the day an exam is added.
 *
 * ⚠️ **The form works without this controller.** Every field is server-rendered; the
 * controller only hides what the referential says is irrelevant and refills the lists. If the
 * JavaScript fails to load, the administrator sees every field and the server-side validations
 * still refuse an inconsistent combination — a degraded screen, not a broken one.
 */
export default class extends Controller {
  static targets = [
    "country", "system", "cycle", "track", "trackRow",
    "exam", "series", "seriesRow", "sessionTrack", "sessionTrackRow", "optionRow",
    "languageRow", "candidateRow",
    "levelHint", "year", "scope", "organizingSchoolRow"
  ]
  static values = { url: String, kind: String }

  connect() {
    this.refresh()
  }

  /**
   * ⚠️ **Anti-rebond — et le délai a été MESURÉ, pas choisi.**
   *
   * `refresh` est câblé sur le `change` de sept sélecteurs, et `connect` s'ajoute par-dessus.
   * À 80 ms, la sonde du 2026-09-20 comptait encore **quatre** requêtes au chargement : ce
   * n'est pas la frappe qui les rapproche, c'est SlimSelect qui décore ses douze sélecteurs
   * l'un après l'autre et fait remonter un `change` à chacun — plus lentement que 80 ms.
   *
   * ⚠️ Le commentaire d'origine affirmait que 80 ms les regroupait. Il était faux, et seule
   * la mesure au navigateur pouvait le dire : les trois requêtes en trop rendaient `304`.
   */
  refresh() {
    // A `change` raised by this controller's own repopulation (SlimSelect's `setData` fires
    // one on the exam select) is not a choice: refetching on it looped without end.
    if (this.populating) return

    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.fetchReferential(), 250)
  }

  async fetchReferential() {
    // ⛔ **Les cibles du CONTEXTE SCOLAIRE n'existent plus dans le flux concours** (arbitrage
    // du 2026-09-21) : un concours n'a ni sous-système, ni ordre, ni filière. Sans ces
    // gardes, Stimulus lève sur `this.cycleTarget` dès le premier `connect()`, et c'est la
    // cascade ENTIÈRE du §11 qui meurt — en silence, comme le 2026-09-20 où le contrôleur
    // tournait dans le mauvais paquet.
    if (this.hasTrackRowTarget && this.hasCycleTarget) {
      // §1 — the track question only exists for secondary education.
      this.toggle(this.trackRowTarget, this.value(this.cycleTarget) === "SECONDARY")
    }

    const params = new URLSearchParams()
    this.appendIfPresent(params, "country_code", this.countryTarget)
    if (this.hasSystemTarget) this.appendIfPresent(params, "education_system", this.systemTarget)
    if (this.hasCycleTarget) this.appendIfPresent(params, "cycle_code", this.cycleTarget)
    if (this.hasCycleTarget && this.value(this.cycleTarget) === "SECONDARY") {
      this.appendIfPresent(params, "education_track", this.trackTarget)
    }
    this.appendIfPresent(params, "exam_id", this.examTarget)
    // ⚠️ **Le flux voyage avec la requête** : sans lui, le serveur renverrait les deux
    // familles d'examens dans la liste du concours.
    if (this.kindValue) params.append("kind", this.kindValue)
    if (this.hasYearTarget) this.appendIfPresent(params, "year", this.yearTarget)

    let payload
    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: "application/json" }
      })
      if (!response.ok) return
      payload = await response.json()
    } catch {
      // See the class header: a failed lookup leaves the server-rendered form intact.
      return
    }

    this.populating = true
    setTimeout(() => { this.populating = false }, 0)
    this.replaceOptions(this.examTarget, payload.exams)
    if (this.hasSeriesTarget) this.replaceOptions(this.seriesTarget, payload.series)
    // ⚠️ **`has…Target` ici, et pas sur la série** : ce contrôleur ne sert que l'écran de
    // création, mais le parcours est le premier champ qu'un gabarit peut légitimement ne pas
    // porter (l'étape 3 revisitée le rend côté serveur). Sans la garde, Stimulus lève, et la
    // cascade ENTIÈRE du §11 meurt — le défaut exact mesuré au navigateur ce matin, où rien
    // n'apparaissait en console.
    if (this.hasSessionTrackTarget) {
      this.replaceOptions(this.sessionTrackTarget, payload.session_tracks)
    }
    this.applyVisibility(payload.visible_fields)
    this.showLevel(payload.class_names)
  }

  /**
   * §11 — the conditional table, applied. `visible_fields` is the Ruby answer; this method
   * only translates it into `hidden` attributes.
   */
  applyVisibility(fields) {
    if (!fields) return

    if (this.hasSeriesRowTarget) this.toggle(this.seriesRowTarget, fields.series)
    if (this.hasSessionTrackRowTarget) {
      this.toggle(this.sessionTrackRowTarget, fields.session_track)
    }
    if (this.hasOptionRowTarget) this.toggle(this.optionRowTarget, fields.option_name)
    if (this.hasLanguageRowTarget) this.toggle(this.languageRowTarget, fields.second_language)
    if (this.hasCandidateRowTarget) this.toggle(this.candidateRowTarget, fields.candidate_type)

    // §3.A — the organising institution needs BOTH conditions: an entrance contest, and a
    // scope of "institution". Either one alone is not the case the rule describes.
    if (this.hasOrganizingSchoolRowTarget && this.hasScopeTarget) {
      const localContest = fields.organizing_school && this.value(this.scopeTarget) === "school"
      this.toggle(this.organizingSchoolRowTarget, localContest)
    }
  }

  /**
   * §1 — *"The level must be derived automatically from the exam."* Shown as a hint and never
   * as an input: the derivation lives in `class_names_exams`, and offering it as a field would
   * let somebody pick Terminale and then BEPC — the exact case the specification forbids.
   */
  showLevel(classNames) {
    if (!this.hasLevelHintTarget) return

    const labels = (classNames || []).map((row) => row.label)
    this.levelHintTarget.textContent = labels.length
      ? this.levelHintTarget.dataset.prefix
        ? `${this.levelHintTarget.dataset.prefix} ${labels.join(" · ")}`
        : labels.join(" · ")
      : ""
  }

  // --- helpers ---------------------------------------------------------------------------

  value(target) {
    return target && target.value ? target.value : ""
  }

  appendIfPresent(params, key, target) {
    const value = this.value(target)
    if (value) params.append(key, value)
  }

  /**
   * ⛔ **LE `<select>` NATIF EST INVISIBLE — mesuré à l'écran le 2026-09-20, et 40 tests verts
   * ne le voyaient pas.**
   *
   * GO4LEARN habille ses sélecteurs avec **SlimSelect** (`utils/g4l_select.js`, 42 `<select>`
   * dans 27 vues). Le paquet met le `<select>` en `aria-hidden` et rend **son propre DOM** à
   * côté. Réécrire les `<option>` natives — ce que faisait la première rédaction — ne change
   * donc rien à ce qui est AFFICHÉ : la liste des séries restait figée sur « Choisir… » après
   * le choix de l'examen, et toute la cascade du §11 était morte à l'écran.
   *
   * 🔴 Le test d'intégration ne pouvait pas l'attraper : il lit le HTML servi, où les options
   * sont bien là. C'est le navigateur qui a dit non — `element is not visible`.
   *
   * ⚠️ **`setData` et non `destroy()` + ré-init** : reconstruire l'instance perdrait le
   * déshabillage du `<select>` que `decorate()` fait avant construction (piège n° 1 du module :
   * SlimSelect recopie la classe et le style EN LIGNE sur le panneau).
   *
   * ⚠️ **L'option d'attente doit être REDÉSIGNÉE** : le paquet ne la reconnaît qu'à
   * `placeholder: true`, et une liste repeuplée sans elle affiche une pilule vide.
   *
   * La sélection courante est préservée quand elle est encore offerte : sans cela, refiller la
   * liste remettrait à zéro l'examen que l'administrateur vient de choisir.
   */
  replaceOptions(target, rows) {
    if (!target || !rows) return

    const previous = target.value
    const blank = target.querySelector('option[value=""]')
    const blankLabel = blank ? blank.textContent : null
    const keep = rows.some((row) => String(row.id) === String(previous))

    target.innerHTML = ""
    if (blank) target.appendChild(blank)

    rows.forEach((row) => {
      const option = document.createElement("option")
      option.value = row.id
      option.textContent = row.label
      target.appendChild(option)
    })

    if (keep) target.value = previous

    this.refreshSlimSelect(target, rows, { blankLabel, selected: keep ? previous : "" })
  }

  /**
   * Répercute la liste sur le sélecteur HABILLÉ, quand il y en a un.
   *
   * ⚠️ **Silencieux quand SlimSelect n'est pas là** : le module se charge en `import()`
   * dynamique, et il peut ne pas encore être prêt au premier `connect`. Le `<select>` natif
   * reste alors correct, et la prochaine passe rattrapera — un écran dégradé, pas cassé.
   */
  refreshSlimSelect(target, rows, { blankLabel, selected }) {
    const instance = target.g4lSelect
    if (!instance || typeof instance.setData !== "function") return

    const data = []
    if (blankLabel !== null) {
      data.push({ text: blankLabel, value: "", placeholder: true, selected: !selected })
    }
    rows.forEach((row) => {
      data.push({ text: row.label, value: String(row.id), selected: String(row.id) === String(selected) })
    })

    try {
      instance.setData(data)
    } catch {
      // Une liste que le paquet refuse laisse le `<select>` natif faire foi.
    }
  }

  toggle(element, visible) {
    if (!element) return
    element.hidden = !visible
    if (visible) return

    // A hidden field must not be submitted: a series left over from a previous exam would
    // otherwise reach the server and be refused by `series_is_offered_by_exam` — a refusal
    // about a field the administrator can no longer see.
    //
    // ⚠️ **Et le sélecteur HABILLÉ doit être vidé lui aussi** : remettre `select.value = ""`
    // laisse SlimSelect afficher l'ancien choix, et l'écran dirait « Série C » sur un champ
    // qui n'envoie plus rien.
    element.querySelectorAll("select, input").forEach((field) => {
      if (field.type === "hidden") return

      field.value = ""
      if (field.g4lSelect && typeof field.g4lSelect.setSelected === "function") {
        try {
          field.g4lSelect.setSelected("")
        } catch {
          /* le champ natif est déjà vidé, c'est ce qui part au serveur */
        }
      }
    })
  }
}
